/**
 * The IDS semantic token *values* the approved prototype
 * (`specs/my-info-ui/my-info-ui-prototype.html`) is built from.
 *
 * These are literals rather than theme lookups because Interstate exposes its colour tokens through
 * `Typography`'s `color` prop and its own components' styling — not through anything `Box`'s `sx`
 * can name. The established repo idiom for a bordered surface is a literal in `sx` (see
 * `welcome/react/src/components/SupportCard.tsx`), so this file follows it while keeping every value
 * in **one** place: the frame spans four components, and a hex repeated across four files is the
 * thing that drifts.
 *
 * Each constant records the token it stands for, so a future swap to real theme lookups is a
 * mechanical edit here rather than a hunt.
 */

/** `--ids-sem-color-surface-default` — the card's own surface. */
export const SURFACE_DEFAULT = "#ffffff";

/** `--ids-sem-color-border-muted` — the card's 1px outline. */
export const BORDER_MUTED = "#b2b2b2";

/**
 * `--ids-sem-color-surface-muted` — the internal rules: below the identity row and between form
 * sections. Deliberately lighter than {@link BORDER_MUTED}: the card's edge is the strong line and
 * the divisions inside it are the quiet ones, which is what keeps the panel reading as one object.
 */
export const SURFACE_MUTED = "#e3e3e3";

/** `--ids-sem-color-surface-interactive-rest` — the avatar's fill. */
export const SURFACE_INTERACTIVE_REST = "#005ba8";

/** `--ids-sem-color-surface-subdued` — the Login History header band. */
export const SURFACE_SUBDUED = "#f4f4f4";

/** `--ids-sem-color-on-surface-muted` — column headers and other secondary text. */
export const ON_SURFACE_MUTED = "#6d6d6d";

/** `--ids-sem-color-on-surface-inverse` — the avatar's initial, on {@link SURFACE_INTERACTIVE_REST}. */
export const ON_SURFACE_INVERSE = "#ffffff";

/**
 * `--ids-sem-space-4`. The prototype's padding for the identity row and every form section, and the
 * horizontal inset the tab strip shares with them — so content lines up down the whole card.
 */
export const SPACE_SECTION = "24px";

/** `--ids-sem-space-3`. The row gap inside a form grid, and the gap between stacked blocks. */
export const SPACE_BLOCK = "16px";
